import pino from 'pino';
import pinoPretty from 'pino-pretty';
import { existsSync, mkdirSync } from 'fs';
import { promises as fs } from 'fs';
import path from 'path';

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

export interface LogContext {
  user?: string;
  operation?: string;
  duration?: number;
  tweetCount?: number;
  filePath?: string;
  requestId?: string;
  [key: string]: any;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  stack?: string;
}

export class Logger {
  private static instance: Logger;
  private pinoLogger: pino.Logger;
  private logLevel: LogLevel = LogLevel.INFO;
  private logFile?: string;
  private enableConsole: boolean = true;
  private enableFile: boolean = false;

  private constructor() {
    // Create Pino logger with Bun-compatible configuration
    // Note: Avoiding transports to prevent worker thread issues with Bun

    const loggerConfig: pino.LoggerOptions = {
      level: this.mapLogLevel(this.logLevel),
      timestamp: pino.stdTimeFunctions.isoTime,
      formatters: {
        level: (label) => ({ level: label }),
      },
      serializers: {
        error: pino.stdSerializers.err,
        req: pino.stdSerializers.req,
        res: pino.stdSerializers.res,
      },
      // Custom mixin to add context information
      mixin: () => ({
        timestamp: new Date().toISOString(),
      }),
    };

    // Create base Pino logger without transports
    this.pinoLogger = pino(loggerConfig);

    // Handle file logging separately if enabled
    if (this.enableFile && this.logFile) {
      this.setupFileTransport();
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  configure(options: {
    level?: LogLevel;
    logFile?: string;
    enableConsole?: boolean;
    enableFile?: boolean;
  }): void {
    if (options.level !== undefined) {
      this.logLevel = options.level;
      this.pinoLogger.level = this.mapLogLevel(this.logLevel);
    }
    if (options.logFile) {
      this.logFile = options.logFile;
      this.enableFile = true;
      // Configure file transport if needed
      if (this.enableFile && this.logFile) {
        this.setupFileTransport();
      }
    }
    if (options.enableConsole !== undefined) this.enableConsole = options.enableConsole;
    if (options.enableFile !== undefined) this.enableFile = options.enableFile;
  }

  private mapLogLevel(level: LogLevel): pino.LevelWithSilent {
    switch (level) {
      case LogLevel.ERROR: return 'error';
      case LogLevel.WARN: return 'warn';
      case LogLevel.INFO: return 'info';
      case LogLevel.DEBUG: return 'debug';
      case LogLevel.TRACE: return 'trace';
      default: return 'info';
    }
  }

  private setupFileTransport(): void {
    if (!this.logFile) return;

    try {
      const logDir = path.dirname(this.logFile);
      if (!existsSync(logDir)) {
        mkdirSync(logDir, { recursive: true });
      }

      // Create a file transport logger for production
      if (process.env.NODE_ENV === 'production' || this.enableFile) {
        const fileLogger = pino(
          {
            level: this.mapLogLevel(this.logLevel),
            timestamp: pino.stdTimeFunctions.isoTime,
          },
          pino.destination(this.logFile)
        );

        // Replace the main logger with the file logger in production
        if (process.env.NODE_ENV === 'production') {
          this.pinoLogger = fileLogger;
        }
      }
    } catch (error) {
      // Fallback to console if file logging fails
      console.error('Failed to setup file logging:', error);
    }
  }

  // Create child logger for specific contexts
  child(bindings: Record<string, any>): Logger {
    const childLogger = new Logger();
    childLogger.pinoLogger = this.pinoLogger.child(bindings);
    childLogger.logLevel = this.logLevel;
    childLogger.logFile = this.logFile;
    childLogger.enableConsole = this.enableConsole;
    childLogger.enableFile = this.enableFile;
    return childLogger;
  }

  error(message: string, context?: LogContext, error?: Error): void {
    const logData: any = { ...context };
    if (error) {
      logData.error = error;
    }
    this.pinoLogger.error(logData, message);
  }

  warn(message: string, context?: LogContext): void {
    this.pinoLogger.warn(context || {}, message);
  }

  info(message: string, context?: LogContext): void {
    this.pinoLogger.info(context || {}, message);
  }

  debug(message: string, context?: LogContext): void {
    this.pinoLogger.debug(context || {}, message);
  }

  trace(message: string, context?: LogContext): void {
    this.pinoLogger.trace(context || {}, message);
  }

  // Specialized logging methods
  scrapeStart(username: string, maxTweets: number): void {
    this.info(`Starting scrape operation for @${username}`, {
      operation: 'scrape_start',
      user: username,
      maxTweets
    });
  }

  scrapeComplete(username: string, tweetCount: number, duration: number): void {
    this.info(`Scrape completed for @${username}`, {
      operation: 'scrape_complete',
      user: username,
      tweetCount,
      duration
    });
  }

  apiRequest(method: string, path: string, statusCode?: number, duration?: number): void {
    const ctx: LogContext = { operation: 'api_request', method, path };
    if (statusCode !== undefined) ctx.statusCode = statusCode;
    if (duration !== undefined) ctx.duration = duration;
    this.info(`${method} ${path}`, ctx);
  }

  authFailure(reason: string, context?: LogContext): void {
    this.warn(`Authentication failed: ${reason}`, {
      operation: 'auth_failure',
      ...context
    });
  }

  rateLimitExceeded(identifier: string, limit: number): void {
    this.warn(`Rate limit exceeded for ${identifier}`, {
      operation: 'rate_limit_exceeded',
      identifier,
      limit
    });
  }
}

// Export singleton instance
export const logger: Logger = Logger.getInstance();

// Environment-based logger configuration
export interface LoggerConfig {
  level?: LogLevel;
  logFile?: string;
  enableConsole?: boolean;
  enableFile?: boolean;
  pretty?: boolean;
  consoleLevel?: LogLevel;
}

// Parse environment variables for logger configuration
const parseLoggerConfig = (): LoggerConfig => {
  return {
    level: process.env.LOG_LEVEL ? parseInt(process.env.LOG_LEVEL) : LogLevel.INFO,
    logFile: process.env.LOG_FILE,
    enableConsole: process.env.LOG_CONSOLE !== 'false', // Default to true
    enableFile: process.env.LOG_TO_FILE === 'true',
    pretty: process.env.LOG_PRETTY === 'true',
    consoleLevel: process.env.LOG_CONSOLE_LEVEL ? parseInt(process.env.LOG_CONSOLE_LEVEL) : LogLevel.INFO,
  };
};

// Initialize with environment-based configuration
const config = parseLoggerConfig();
logger.configure(config);

// Export configuration utilities
export const configureLogger = (config: LoggerConfig): void => {
  logger.configure(config);
};

export const getLoggerConfig = (): LoggerConfig => {
  return parseLoggerConfig();
};

// Pino-specific configuration helpers
export const enablePrettyLogging = (): void => {
  process.env.LOG_PRETTY = 'true';
  logger.configure({ pretty: true });
};

export const setLogLevel = (level: LogLevel): void => {
  logger.configure({ level });
};

export const setLogFile = (filePath: string): void => {
  logger.configure({ logFile: filePath, enableFile: true });
};

// Export additional Pino utilities for advanced usage
export { pino };
export default logger;

/**
 * # PODx Pino Logger Configuration Guide
 *
 * This logger uses Pino v9.9.4 with enhanced features for PODx applications.
 *
 * ## Environment Variables
 *
 * ### Basic Configuration
 * - `LOG_LEVEL`: Log level (0=ERROR, 1=WARN, 2=INFO, 3=DEBUG, 4=TRACE)
 * - `LOG_FILE`: Path to log file for file output
 * - `LOG_TO_FILE`: Enable file logging (true/false)
 * - `LOG_CONSOLE`: Enable console logging (true/false)
 * - `LOG_PRETTY`: Force pretty printing (true/false)
 * - `LOG_CONSOLE_LEVEL`: Separate level for console output
 *
 * ### Production Settings
 * ```bash
 * LOG_LEVEL=2
 * LOG_FILE=/var/log/podx/app.log
 * LOG_TO_FILE=true
 * LOG_PRETTY=false
 * NODE_ENV=production
 * ```
 *
 * ### Development Settings
 * ```bash
 * LOG_LEVEL=3
 * LOG_PRETTY=true
 * NODE_ENV=development
 * ```
 *
 * ## Usage Examples
 *
 * ### Basic Logging
 * ```typescript
 * import { logger } from '@podx/core/logger';
 *
 * logger.info('Application started');
 * logger.error('Database connection failed', { error: err });
 * logger.debug('Processing user', { userId: '123', operation: 'update' });
 * ```
 *
 * ### Specialized Logging
 * ```typescript
 * // Scraper operations
 * logger.scrapeStart('username', 100);
 * logger.scrapeComplete('username', 85, 1250);
 *
 * // API requests
 * logger.apiRequest('GET', '/api/users', 200, 45);
 *
 * // Authentication
 * logger.authFailure('invalid_token', { userId: '123' });
 * ```
 *
 * ### Child Loggers
 * ```typescript
 * import { createModuleLogger, createServiceLogger } from '@podx/core/logger';
 *
 * const scraperLogger = createModuleLogger('scraper');
 * const apiLogger = createServiceLogger('api', '1.0.0');
 *
 * scraperLogger.info('Starting scrape operation');
 * apiLogger.debug('Processing request', { requestId: 'abc-123' });
 * ```
 *
 * ## Log Output Formats
 *
 * ### Development (Pretty)
 * ```
 * [2024-01-15 10:30:45.123] INFO  🔍 Starting scrape operation for @username | operation=scrape_start user=username maxTweets=100
 * ```
 *
 * ### Production (JSON)
 * ```json
 * {"level":"info","time":1705312245123,"msg":"Starting scrape operation for @username","operation":"scrape_start","user":"username","maxTweets":100}
 * ```
 *
 * ## Features
 *
 * - **High Performance**: Pino is one of the fastest Node.js loggers
 * - **Structured Logging**: JSON format with searchable fields
 * - **Child Loggers**: Context-aware logging for different modules
 * - **Pretty Printing**: Human-readable output in development
 * - **File Rotation**: Automatic log file management
 * - **Error Serialization**: Proper error object handling
 * - **Type Safety**: Full TypeScript support
 */

// Utility functions for creating child loggers
export const createModuleLogger = (moduleName: string): Logger => {
  return logger.child({ module: moduleName });
};

// Performance Monitoring and Benchmarking
export interface LoggerBenchmarkResult {
  operation: string;
  iterations: number;
  totalTime: number;
  averageTime: number;
  operationsPerSecond: number;
}

export const benchmarkLogger = async (
  iterations: number = 10000
): Promise<LoggerBenchmarkResult[]> => {
  const results: LoggerBenchmarkResult[] = [];

  // Benchmark basic info logging
  const startInfo = performance.now();
  for (let i = 0; i < iterations; i++) {
    logger.info(`Benchmark message ${i}`, { iteration: i });
  }
  const infoTime = performance.now() - startInfo;

  results.push({
    operation: 'info_logging',
    iterations,
    totalTime: infoTime,
    averageTime: infoTime / iterations,
    operationsPerSecond: iterations / (infoTime / 1000),
  });

  // Benchmark child logger
  const childLogger = logger.child({ benchmark: 'child' });
  const startChild = performance.now();
  for (let i = 0; i < iterations; i++) {
    childLogger.debug(`Child benchmark ${i}`, { iteration: i });
  }
  const childTime = performance.now() - startChild;

  results.push({
    operation: 'child_logger',
    iterations,
    totalTime: childTime,
    averageTime: childTime / iterations,
    operationsPerSecond: iterations / (childTime / 1000),
  });

  // Benchmark specialized methods
  const startSpecialized = performance.now();
  for (let i = 0; i < iterations / 10; i++) { // Fewer iterations for specialized methods
    logger.apiRequest('GET', `/api/benchmark/${i}`, 200, Math.random() * 100);
  }
  const specializedTime = performance.now() - startSpecialized;
  const specializedIterations = iterations / 10;

  results.push({
    operation: 'specialized_methods',
    iterations: specializedIterations,
    totalTime: specializedTime,
    averageTime: specializedTime / specializedIterations,
    operationsPerSecond: specializedIterations / (specializedTime / 1000),
  });

  return results;
};

export const logBenchmarkResults = async (iterations: number = 10000): Promise<void> => {
  logger.info('Starting logger performance benchmark', { iterations });

  const results = await benchmarkLogger(iterations);

  results.forEach(result => {
    logger.info('Benchmark result', {
      operation: result.operation,
      iterations: result.iterations,
      totalTime: `${result.totalTime.toFixed(2)}ms`,
      averageTime: `${result.averageTime.toFixed(4)}ms`,
      operationsPerSecond: `${result.operationsPerSecond.toFixed(0)} ops/sec`,
    });
  });

  logger.info('Logger performance benchmark completed');
};

// API Compatibility Test
export const testLoggerAPI = (): void => {
  console.log('🧪 Testing Pino Logger API compatibility...');

  // Test basic logging methods
  logger.info('API compatibility test - info');
  logger.warn('API compatibility test - warn');
  logger.error('API compatibility test - error');
  logger.debug('API compatibility test - debug');

  // Test specialized methods
  logger.scrapeStart('testuser', 10);
  logger.scrapeComplete('testuser', 8, 1000);
  logger.apiRequest('GET', '/api/test', 200, 50);
  logger.authFailure('test_failure');
  logger.rateLimitExceeded('test_identifier', 100);

  // Test child logger
  const childLogger = logger.child({ test: 'compatibility' });
  childLogger.info('Child logger test');

  console.log('✅ Pino Logger API compatibility test completed');
};

export const createServiceLogger = (serviceName: string, serviceVersion?: string): Logger => {
  const bindings: Record<string, any> = { service: serviceName };
  if (serviceVersion) bindings.serviceVersion = serviceVersion;
  return logger.child(bindings);
};

export const createRequestLogger = (requestId: string, userId?: string): Logger => {
  const bindings: Record<string, any> = { requestId };
  if (userId) bindings.userId = userId;
  return logger.child(bindings);
};

export const createScraperLogger = (scraperName: string, target?: string): Logger => {
  const bindings: Record<string, any> = { scraper: scraperName };
  if (target) bindings.target = target;
  return logger.child(bindings);
};

export const createApiLogger = (method: string, path: string): Logger => {
  return logger.child({ api: { method, path } });
};
